import {
  RecordContextProvider,
  useCanAccess,
  useListContext,
  useRecordContext,
  useTranslate,
} from "ra-core";
import { CreateButton } from "@/components/admin/create-button";
import { DataTable } from "@/components/admin/data-table";
import { DateField } from "@/components/admin/date-field";
import { EditButton } from "@/components/admin/edit-button";
import { ExportButton } from "@/components/admin/export-button";
import { List } from "@/components/admin/list";
import { ReferenceField } from "@/components/admin/reference-field";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { Badge } from "@/components/ui/badge";
import { useIsMobile } from "@/hooks/use-mobile";
import { Link } from "react-router";

import { LanguageToggle } from "../layout/LanguageToggle";
import { TopToolbar } from "../layout/TopToolbar";
import {
  DownloadInvoiceButton,
  PreviewInvoiceButton,
} from "./DownloadInvoiceButton";
import { formatUsd, tradeTypeChoices, type Shipment } from "./types";

// Phone pages have no top bar here, so the language switch sits in the toolbar.
const ListActions = () => (
  <TopToolbar>
    {useIsMobile() ? <LanguageToggle /> : null}
    <ExportButton />
    <CreateButton label="egi.shipments.new" />
  </TopToolbar>
);

const useFilters = () => {
  const { canAccess: isAdmin } = useCanAccess({
    resource: "sales",
    action: "list",
  });
  return [
    <ReferenceInput key="c" source="company_id" reference="companies" alwaysOn>
      <AutocompleteInput label="egi.fields.customer" helperText={false} />
    </ReferenceInput>,
    ...(isAdmin
      ? [
          <ReferenceInput key="s" source="sales_id" reference="sales" alwaysOn>
            <SelectInput
              label="egi.fields.salesperson"
              optionText={(s) => `${s.first_name} ${s.last_name}`}
              helperText={false}
            />
          </ReferenceInput>,
        ]
      : []),
    <SelectInput
      key="tt"
      source="trade_type"
      label="egi.trade_type.label"
      choices={tradeTypeChoices}
      helperText={false}
    />,
    <SelectInput
      key="st"
      source="status"
      choices={[
        { id: "active", name: "egi.status.active" },
        { id: "void", name: "egi.status.void" },
      ]}
      helperText={false}
    />,
  ];
};

const TotalCell = () => {
  const r = useRecordContext<Shipment>();
  if (!r) return null;
  return (
    <span
      className={`tabular-nums ${r.status === "void" ? "line-through text-muted-foreground" : ""}`}
    >
      {formatUsd(Number(r.total))}
    </span>
  );
};

const StatusCell = () => {
  const r = useRecordContext<Shipment>();
  const translate = useTranslate();
  if (!r) return null;
  const intercompany =
    r.trade_type === "intercompany" ? (
      <Badge variant="outline" className="border-sky-600/50 text-sky-700">
        {translate("egi.trade_type.intercompany")}
      </Badge>
    ) : null;
  return (
    <span className="inline-flex flex-wrap gap-1 justify-end">
      {intercompany}
      <SignStatus />
    </span>
  );
};

const SignStatus = () => {
  const r = useRecordContext<Shipment>();
  const translate = useTranslate();
  if (!r) return null;
  if (r.status === "void")
    return <Badge variant="outline">{translate("egi.status.void")}</Badge>;
  if (r.signed_at)
    return (
      <Badge variant="outline" className="border-green-600/50 text-green-700">
        {translate("egi.sign.badge")}
      </Badge>
    );
  return null;
};

const RowEditButton = () => {
  const r = useRecordContext<Shipment>();
  return r && !r.signed_at ? <EditButton iconOnly /> : null;
};

const PageTotal = () => {
  const translate = useTranslate();
  const { data } = useListContext<Shipment>();
  if (!data?.length) return null;
  const sum = data
    .filter((s) => s.status === "active")
    .reduce((acc, s) => acc + Number(s.total), 0);
  return (
    <div className="text-sm text-muted-foreground text-right pt-2">
      {translate("egi.shipments.page_total")}{" "}
      <span className="font-semibold text-foreground tabular-nums">
        {formatUsd(sum)}
      </span>
    </div>
  );
};

// Phones get a one-column card list: the desktop table would scroll sideways.
const MobileRows = () => {
  const { data } = useListContext<Shipment>();
  return (
    <div className="flex flex-col divide-y">
      {data?.map((r) => (
        <RecordContextProvider key={r.id} value={r}>
          <Link
            to={`/shipments/${r.id}/show`}
            className="flex justify-between gap-3 py-3 px-1"
          >
            <div className="min-w-0">
              <div className="font-medium tabular-nums">{r.invoice_number}</div>
              <div className="text-sm text-muted-foreground truncate">
                {new Date(`${r.invoice_date}T00:00:00`).toLocaleDateString()}{" "}
                <ReferenceField
                  source="company_id"
                  reference="companies"
                  link={false}
                />
              </div>
            </div>
            <div className="text-right shrink-0 flex flex-col items-end gap-1">
              <TotalCell />
              <StatusCell />
            </div>
          </Link>
        </RecordContextProvider>
      ))}
    </div>
  );
};

export const ShipmentList = () => {
  const isMobile = useIsMobile();
  return (
    <div className={isMobile ? "px-4" : undefined}>
      <List
        filters={useFilters()}
        actions={<ListActions />}
        sort={{ field: "invoice_date", order: "DESC" }}
        perPage={50}
      >
        {isMobile ? (
          <MobileRows />
        ) : (
          <DataTable rowClick="show" bulkActionButtons={false}>
            <DataTable.Col
              source="invoice_number"
              label="egi.fields.invoice_number"
            />
            <DataTable.Col source="invoice_date" label="egi.fields.date">
              <DateField source="invoice_date" />
            </DataTable.Col>
            <DataTable.Col source="company_id" label="egi.fields.customer">
              <ReferenceField
                source="company_id"
                reference="companies"
                link={false}
              />
            </DataTable.Col>
            <DataTable.Col source="sales_id" label="egi.fields.salesperson">
              <ReferenceField
                source="sales_id"
                reference="sales"
                link={false}
              />
            </DataTable.Col>
            <DataTable.Col
              source="total"
              label="egi.fields.total"
              className="text-right"
            >
              <TotalCell />
            </DataTable.Col>
            <DataTable.Col label={false}>
              <StatusCell />
            </DataTable.Col>
            <DataTable.Col label={false}>
              <div className="flex gap-2 justify-end">
                {/* Signed invoices are locked, same rule as the detail page. */}
                <RowEditButton />
                <PreviewInvoiceButton />
                <DownloadInvoiceButton />
              </div>
            </DataTable.Col>
          </DataTable>
        )}
        <PageTotal />
      </List>
    </div>
  );
};
